import uuid from "uuid";
import { client as request } from "../../api/request";

/**
 * 経費計算書
 */
export interface Expense {
  id: string;
  title: string; // 件名
  date: string; // 発行日, yyyy-MM-dd
  vendorId: string; // 発注先ID
  vendorName: string; // 発注先社名
  estimateId: string; // 見積書ID
  items: ExpenseItem[];
}

/**
 * 経費計算書の項目
 */
export interface ExpenseItem {
  id: string;
  name: string; // 品名
  unitTypeId: string; // 単位ID
  unitType: string; // 単位
  estimateUnitPrice: number; // 受注単価
  estimateAmount: number; // 受注数量
  estimateTotal: number; // 受注金額
  orderUnitPrice: number; // 発注単価
  orderTotal: number; // 発注金額
  expenseCategoryId: string; // 経費カテゴリID
  unitPrice: number; // 経費単価
  amount: number; // 経費数量
  total: number; // 経費合計
  memo: string; // 備考
  new: boolean;
  items: ExpenseItem[];
}

/**
 * 経費計算書を取得する
 */
export async function getExpense(expenseId: string): Promise<Expense> {
  const response = await request.get("get_expense_statement.php", {
    id: expenseId
  });
  return response.Expense;
}

export interface SaveExpenseCommand {
  id: string;
  title: string; // 件名
  date: string; // 発行日, yyyy-MM-dd
  vendorId: string; // 発注先ID
  items: ExpenseItem[];
}

export interface SaveExpenseCommandItem {
  id: string;
  name: string; // 品名
  expenseCategoryId: string; // 経費カテゴリID
  unitPrice: number; // 経費単価
  amount: number; // 経費数量
  memo: string; // 備考
  items: SaveExpenseCommandItem[];
}

/**
 * 経費計算書を保存する
 */
export async function saveExpense(command: SaveExpenseCommand) {
  const data = new FormData();
  data.append("id", command.id);
  data.append("title", command.title);
  data.append("date", command.date);
  data.append("vendorId", command.vendorId);
  // TODO: 入れ子の item を追加する良い方法があれば修正
  if (command.items) {
    command.items.forEach((item, i) => {
      data.append(`items[${i}][id]`, item.id);
      data.append(`items[${i}][name]`, item.name);
      data.append(`items[${i}][unitTypeId]`, item.unitTypeId);
      data.append(`items[${i}][unitType]`, item.unitType);
      data.append(
        `items[${i}][estimateUnitPrice]`,
        String(item.estimateUnitPrice)
      );
      data.append(`items[${i}][estimateAmount]`, String(item.estimateAmount));
      data.append(`items[${i}][estimateTotal]`, String(item.estimateTotal));
      data.append(`items[${i}][orderUnitPrice]`, String(item.orderUnitPrice));
      data.append(`items[${i}][orderTotal]`, String(item.orderTotal));
      data.append(`items[${i}][expenseCategoryId]`, item.expenseCategoryId);
      data.append(`items[${i}][unitPrice]`, String(item.unitPrice));
      data.append(`items[${i}][amount]`, String(item.amount));
      data.append(`items[${i}][total]`, String(item.total));
      data.append(`items[${i}][memo]`, item.memo);
      data.append(`items[${i}][new]`, String(item.new));
      if (item.items) {
        command.items[i].items.forEach((subitem, j) => {
          data.append(`items[${i}][items][${j}][id]`, subitem.id);
          data.append(`items[${i}][items][${j}][name]`, subitem.name);
          data.append(
            `items[${i}][items][${j}][unitTypeId]`,
            subitem.unitTypeId
          );
          data.append(`items[${i}][items][${j}][unitType]`, subitem.unitType);
          data.append(
            `items[${i}][items][${j}][estimateUnitPrice]`,
            String(subitem.estimateUnitPrice)
          );
          data.append(
            `items[${i}][items][${j}][estimateAmount]`,
            String(subitem.estimateAmount)
          );
          data.append(
            `items[${i}][items][${j}][estimateTotal]`,
            String(subitem.estimateTotal)
          );
          data.append(
            `items[${i}][items][${j}][orderUnitPrice]`,
            String(subitem.orderUnitPrice)
          );
          data.append(
            `items[${i}][items][${j}][orderTotal]`,
            String(subitem.orderTotal)
          );
          data.append(
            `items[${i}][items][${j}][expenseCategoryId]`,
            subitem.expenseCategoryId
          );
          data.append(
            `items[${i}][items][${j}][unitPrice]`,
            String(subitem.unitPrice)
          );
          data.append(
            `items[${i}][items][${j}][amount]`,
            String(subitem.amount)
          );
          data.append(`items[${i}][items][${j}][total]`, String(subitem.total));
          data.append(`items[${i}][items][${j}][memo]`, subitem.memo);
          data.append(`items[${i}][items][${j}][new]`, String(subitem.new));
        });
      }
    });
  }
  await request.post("save_expense_statement.php", data);
}

export async function newExpense(id: string): Promise<void> {
  const data = new FormData();
  data.append("estimate_id", id);
  await request.post("create_expense_statement.php", data);
}

/**
 * 新しい経費項目を作成する
 */
export function newExpenseItem() {
  return {
    id: uuid(),
    name: "",
    unitTypeId: "",
    unitType: "",
    estimateUnitPrice: 0,
    estimateAmount: 0,
    estimateTotal: 0,
    orderUnitPrice: 0,
    orderTotal: 0,
    expenseCategoryId: "",
    unitPrice: 0,
    amount: 0,
    total: 0,
    memo: "",
    new: true,
    items: []
  };
}

/**
 * 経費計算書の一覧を取得する
 */
export async function getExpenses(projectId: string): Promise<Expense[]> {
  const response = await request.get("get_all_expense_statement.php", {
    projectId: projectId
  });
  return response.Expense;
}

/**
 * 経費計算書を削除する
 */
export async function deleteExpenses(id: string): Promise<void> {
  const data = new FormData();
  data.append("id", id);
  await request.post("delete_expense_statement.php", data);
}
